import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const C = {
  text:    '#0f172a',
  muted:   '#475569',
  subtle:  '#64748b',
  faint:   '#94a3b8',
  border:  '#e2e8f0',
  borderL: '#f1f5f9',
  bg:      '#f8fafc',
  white:   '#ffffff',
  blue:    '#2563eb',
  blueL:   '#eff6ff',
};

const STATUS_MAP = {
  NEW:            { bg: '#f1f5f9', color: '#475569' },
  SCANNING:       { bg: '#fffbeb', color: '#b45309' },
  PENDING_DOMAIN: { bg: '#fff7ed', color: '#c2410c' },
  SCANNED:        { bg: C.blueL,   color: C.blue    },
  SENT:           { bg: '#eef2ff', color: '#4338ca' },
  CONVERTED:      { bg: '#ecfdf5', color: '#047857' },
  SCAN_FAILED:    { bg: '#fef2f2', color: '#b91c1c' },
};

const card = {
  background: C.white,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
};

const btnPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '0 16px', height: 38, flexShrink: 0,
  background: C.blue, color: '#fff',
  border: 'none', borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
  whiteSpace: 'nowrap',
};

function InputField({ label, required, value, onChange, type = 'text', placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <div>
      {label && (
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      <input
        value={value}
        onChange={onChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        type={type}
        placeholder={placeholder}
        style={{
          display: 'block', width: '100%', boxSizing: 'border-box',
          height: 40, padding: '0 12px',
          border: `1px solid ${focus ? C.blue : C.border}`,
          borderRadius: 8, outline: 'none',
          background: focus ? C.white : C.bg,
          fontSize: 14, color: C.text,
          transition: 'border-color .15s',
        }}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_MAP[status] || STATUS_MAP.NEW;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 600,
    }}>
      {status}
    </span>
  );
}

function RiskBadge({ score }) {
  const color = score >= 80 ? '#b91c1c' : score >= 60 ? '#c2410c' : score >= 30 ? '#b45309' : '#047857';
  return <span style={{ fontWeight: 700, fontSize: 13, color }}>{score}</span>;
}

function FindingsRow({ lead }) {
  const { t } = useTranslation();
  let findings = null;
  try { findings = lead.findingsSummary ? JSON.parse(lead.findingsSummary) : null; } catch (_) {}
  if (!findings) return null;
  const cve = findings.cve || {};
  const chips = [
    { label: t('PartnerLeads.severityCritical'), count: cve.critical, bg: '#fef2f2', color: '#b91c1c' },
    { label: t('PartnerLeads.severityHigh'),     count: cve.high,     bg: '#fff7ed', color: '#c2410c' },
    { label: t('PartnerLeads.severityMedium'),   count: cve.medium,   bg: '#fffbeb', color: '#b45309' },
    { label: t('PartnerLeads.severityLow'),      count: cve.low,      bg: '#f0fdf4', color: '#15803d' },
  ].filter(c => c.count > 0);
  return (
    <tr style={{ background: '#fafcff', borderBottom: `1px solid ${C.border}` }}>
      <td colSpan={6} style={{ padding: '10px 16px 12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.subtle }}>
            <span style={{ fontWeight: 600, color: C.text }}>{findings.subdomains}</span> {t('PartnerLeads.subdomainsLabel')}
            &nbsp;·&nbsp;
            <span style={{ fontWeight: 600, color: C.text }}>{findings.totalCves}</span> {t('PartnerLeads.cvesTotalLabel')}
          </span>
          {chips.map(c => (
            <span key={c.label} style={{ fontSize: 11, fontWeight: 600, background: c.bg, color: c.color, padding: '2px 8px', borderRadius: 4 }}>
              {c.count} {c.label}
            </span>
          ))}
          {chips.length === 0 && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>{t('PartnerLeads.noCvesFound')}</span>}
        </div>
      </td>
    </tr>
  );
}

export default function PartnerLeads() {
  const { t } = useTranslation();
  const [data, setData]           = useState(null);
  const [page, setPage]           = useState(0);
  const [scanning, setScanning]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [scanForm, setScanForm]   = useState({ domain: '', email: '', name: '' });
  const [scanErr, setScanErr]     = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [expanded, setExpanded]   = useState(null);

  const load = (p = 0) => {
    axiosInstance.get(`/partner/leads?page=${p}&size=20`).then(r => {
      setData(r.data);
      setPage(p);
    });
  };

  useEffect(() => { load(0); }, []); // eslint-disable-line

  // Auto-refresh every 30 s while any lead is still scanning
  useEffect(() => {
    if (!data?.leads?.some(l => l.status === 'SCANNING')) return;
    const id = setInterval(() => load(page), 30_000);
    return () => clearInterval(id);
  }, [data, page]); // eslint-disable-line

  const runScan = async () => {
    setScanErr('');
    if (!scanForm.domain.trim()) { setScanErr(t('PartnerLeads.errorDomainRequired')); return; }
    setScanning(true);
    try {
      const r = await axiosInstance.post('/partner/leads/light-scan', scanForm);
      setScanResult(r.data);
      load(0);
    } catch (e) {
      setScanErr(e.response?.data?.error || t('PartnerLeads.errorScanFailed'));
    } finally {
      setScanning(false);
    }
  };

  const markSent = async (leadId) => {
    try {
      await axiosInstance.post(`/partner/leads/${leadId}/mark-sent`);
      load(page);
    } catch (e) {
      alert(e.response?.data?.error || t('PartnerLeads.errorMarkSent'));
    }
  };

  const kpis  = data?.kpis  || {};
  const quota = data?.quota || {};
  const leads = data?.leads || [];
  const quotaUsed  = quota.used  ?? 0;
  const quotaTotal = quota.total ?? 50;
  const quotaPct   = Math.min(100, (quotaUsed / quotaTotal) * 100);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1000, margin: '0 auto', fontFamily: 'inherit' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{
              width: 36, height: 36, borderRadius: 9, flexShrink: 0,
              background: '#fef9c3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="fas fa-bolt" style={{ color: '#ca8a04', fontSize: 15 }} />
            </span>
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 700, color: C.text, lineHeight: 1 }}>
              {t('PartnerLeads.title')}
            </h1>
          </div>
          <p style={{ margin: '0 0 0 46px', fontSize: 13, color: C.subtle }}>
            {t('PartnerLeads.subtitle')}
          </p>
        </div>
        <button
          onClick={() => { setShowModal(true); setScanResult(null); setScanErr(''); setScanForm({ domain: '', email: '', name: '' }); }}
          style={btnPrimary}>
          <i className="fas fa-bolt" style={{ fontSize: 11 }} />
          {t('PartnerLeads.newLightScan')}
        </button>
      </div>

      {/* ── KPI strip ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: t('PartnerLeads.kpiNew'),        value: kpis.new,            bg: C.bg,      color: C.text    },
          { label: t('PartnerLeads.kpiScanned'),    value: kpis.scanned,        bg: C.blueL,   color: C.blue    },
          { label: t('PartnerLeads.kpiSent'),       value: kpis.sent,           bg: '#eef2ff', color: '#4338ca' },
          { label: t('PartnerLeads.kpiConverted'),  value: kpis.converted,      bg: '#ecfdf5', color: '#047857' },
          { label: t('PartnerLeads.kpiConvRate'),   value: kpis.conversionRate, bg: '#fdf4ff', color: '#7e22ce' },
        ].map(({ label, value, bg, color }) => (
          <div key={label} style={{ ...card, background: bg, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: C.subtle, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color }}>{value ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* ── Quota bar ──────────────────────────────────────────────────────── */}
      <div style={{ ...card, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>{t('PartnerLeads.monthlyQuota')}</span>
            <span style={{ fontSize: 12, color: C.subtle }}>{t('PartnerLeads.quotaUsed', { used: quotaUsed, total: quotaTotal })}</span>
          </div>
          <div style={{ height: 8, background: C.bg, borderRadius: 99, overflow: 'hidden', border: `1px solid ${C.border}` }}>
            <div style={{
              height: '100%', borderRadius: 99,
              background: quotaPct >= 90 ? '#ef4444' : quotaPct >= 70 ? '#f59e0b' : C.blue,
              width: `${quotaPct}%`, transition: 'width .4s',
            }} />
          </div>
        </div>
        <div style={{ fontSize: 13, color: C.subtle, flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: C.text }}>{quotaTotal - quotaUsed}</span> {t('PartnerLeads.remaining')}
        </div>
      </div>

      {/* ── Leads table ────────────────────────────────────────────────────── */}
      <div style={{ ...card, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
              {[t('PartnerLeads.colDomain'), t('PartnerLeads.colContact'), t('PartnerLeads.colRisk'), t('PartnerLeads.colStatus'), t('PartnerLeads.colCreated'), t('PartnerLeads.colActions')].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '48px 20px', textAlign: 'center', color: C.faint, fontSize: 13 }}>
                  <i className="fas fa-bolt" style={{ fontSize: 24, color: C.border, display: 'block', marginBottom: 10 }} />
                  {t('PartnerLeads.emptyState')}
                </td>
              </tr>
            )}
            {leads.map((lead, i) => (
              <React.Fragment key={lead.id}>
              <tr
                onClick={() => setExpanded(e => e === lead.id ? null : lead.id)}
                style={{ borderBottom: `1px solid ${C.borderL}`, background: i % 2 ? '#fafafa' : C.white, cursor: lead.findingsSummary ? 'pointer' : 'default' }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: C.text }}>
                  {lead.prospectDomain}
                  {lead.findingsSummary && (
                    <span style={{ fontSize: 10, color: C.faint, marginLeft: 6, fontWeight: 400 }}>
                      {expanded === lead.id ? '▲' : '▼'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {lead.prospectName  && <div style={{ fontSize: 13, color: C.text }}>{lead.prospectName}</div>}
                  {lead.prospectEmail && <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{lead.prospectEmail}</div>}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {lead.riskScore != null
                    ? <RiskBadge score={lead.riskScore} />
                    : <span style={{ color: C.border }}>—</span>}
                </td>
                <td style={{ padding: '12px 16px' }}><StatusBadge status={lead.status} /></td>
                <td style={{ padding: '12px 16px', color: C.subtle, fontSize: 12 }}>
                  {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-CA') : '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {lead.status === 'SCANNED' && (
                    <button onClick={e => { e.stopPropagation(); markSent(lead.id); }} style={{
                      padding: '4px 10px', fontSize: 11, fontWeight: 500,
                      background: '#eef2ff', color: '#4338ca',
                      border: '1px solid #c7d2fe', borderRadius: 6, cursor: 'pointer',
                    }}>
                      {t('PartnerLeads.markSent')}
                    </button>
                  )}
                  {lead.status === 'CONVERTED' && (
                    <span style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>
                      <i className="fas fa-check" style={{ marginRight: 4 }} />{t('PartnerLeads.client')}
                    </span>
                  )}
                </td>
              </tr>
              {expanded === lead.id && <FindingsRow lead={lead} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* pagination */}
        {data?.totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
            <button disabled={page === 0} onClick={() => load(page - 1)} style={{
              padding: '4px 12px', fontSize: 12, color: C.muted, background: C.bg,
              border: `1px solid ${C.border}`, borderRadius: 6, cursor: page === 0 ? 'default' : 'pointer',
              opacity: page === 0 ? .4 : 1,
            }}>← {t('PartnerLeads.prev')}</button>
            <span style={{ fontSize: 12, color: C.subtle }}>{page + 1} / {data.totalPages}</span>
            <button disabled={page + 1 >= data.totalPages} onClick={() => load(page + 1)} style={{
              padding: '4px 12px', fontSize: 12, color: C.muted, background: C.bg,
              border: `1px solid ${C.border}`, borderRadius: 6, cursor: page + 1 >= data.totalPages ? 'default' : 'pointer',
              opacity: page + 1 >= data.totalPages ? .4 : 1,
            }}>{t('PartnerLeads.next')} →</button>
          </div>
        )}
      </div>

      {/* ── Light Scan modal ────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: C.white, borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 30, height: 30, borderRadius: 7, background: '#fef9c3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-bolt" style={{ fontSize: 13, color: '#ca8a04' }} />
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{t('PartnerLeads.newLightScan')}</span>
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 18, color: C.faint, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
            </div>

            {scanResult ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <span style={{ width: 56, height: 56, borderRadius: '50%', background: C.blueL, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <i className="fas fa-bolt" style={{ fontSize: 22, color: C.blue }} />
                </span>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>{t('PartnerLeads.scanInitiated')}</div>
                <div style={{ fontSize: 13, color: C.subtle }}>{scanResult.domain}</div>
                <div style={{ fontSize: 12, color: C.faint, marginTop: 6 }}>
                  {t('PartnerLeads.scansRemainingThisMonth', { count: scanResult.quotaRemaining })}
                </div>
                <button onClick={() => setShowModal(false)} style={{ ...btnPrimary, marginTop: 20 }}>{t('PartnerLeads.done')}</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <InputField label={t('PartnerLeads.fieldProspectDomain')} required value={scanForm.domain}
                  onChange={e => setScanForm(f => ({ ...f, domain: e.target.value }))}
                  placeholder={t('PartnerLeads.placeholderDomain')} />
                <InputField label={t('PartnerLeads.fieldContactName')} value={scanForm.name}
                  onChange={e => setScanForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={t('PartnerLeads.placeholderName')} />
                <InputField label={t('PartnerLeads.fieldContactEmail')} type="email" value={scanForm.email}
                  onChange={e => setScanForm(f => ({ ...f, email: e.target.value }))}
                  placeholder={t('PartnerLeads.placeholderEmail')} />

                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.subtle }}>
                  {t('PartnerLeads.scanInfo')}
                </div>

                {scanErr && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#b91c1c' }}>
                    <i className="fas fa-exclamation-circle" />{scanErr}
                  </div>
                )}

                <button onClick={runScan} disabled={scanning}
                  style={{ ...btnPrimary, justifyContent: 'center', width: '100%', height: 42, opacity: scanning ? .6 : 1 }}>
                  {scanning
                    ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> {t('PartnerLeads.initiatingScan')}</>
                    : <><i className="fas fa-bolt" style={{ fontSize: 12 }} /> {t('PartnerLeads.runLightScan')}</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
